import type {
    SketchConstraintState,
    SketchSolverIslandSnapshot,
} from '../../../history/solver/SketchSolverRecordTypes';
import {resolveEntityRadiusFromGeometrySnapshot} from '../adapters/SketchEntityParameterAdapters';
import {SketchConstraintFormulaRegistry} from './SketchConstraintFormulaRegistry';
import {SketchDefaultConstraintFormulaRegistryFactory} from './SketchDefaultConstraintFormulaRegistryFactory';
import {SketchDimensionFormulaCompiler} from './SketchDimensionFormulaCompiler';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';
import {SketchParametricOperationIntentCompiler} from './SketchParametricOperationIntentCompiler';
import {SketchParametricSplineEvaluator} from '../core/SketchParametricSplineEvaluator';
import {SketchParametricSystem} from '../core/SketchParametricSystem';
import type {
    SketchParametricSlotStructureParameters,
    SketchParametricSplineMirrorStructure,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';
import type {
    SketchParametricImplicitCurveDescriptor,
    SketchParametricPathSegment,
    SketchParametricPointHandle,
    SketchParametricSplineCurveDescriptor,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import type {
    SketchParametricIslandBuildInput,
    SketchParametricIslandBuildResult,
    SketchParametricEllipseGeometryState,
    SketchParametricParabolaGeometryState,
    SketchParametricSplineLengthState,
    SketchParametricUnsupportedItem,
} from '../../../../types/sketch/solver/SketchParametricIslandTypes';
import type {
    SketchDimensionFormulaCompilerContract,
} from '../../../../types/sketch/solver/SketchDimensionFormulaCompiler';
import type {
    SketchParametricOperationIntentCompilerContract,
} from '../../../../types/sketch/solver/SketchParametricOperationIntentCompiler';
import type {
    SketchParametricUnsupportedOperationIntent,
} from '../../../../types/sketch/solver/SketchParametricOperationIntent';
import type {SerializablePlainData} from '../../../history/domain/SerializablePlainData';

/**
 * 归一化 island 边类型标识。
 *
 * 说明：collector 的 `entityKind` 来自运行时 `objectType`（如 `Line`/`Circle`/`Arc`），
 * 与本模块的小写语义值统一在此收口。
 *
 * @param entityKind 原始类型标识。
 * @returns 小写归一化标识。
 */
function normalizeEdgeKind(entityKind: string): string {
    return entityKind.toLowerCase();
}

/**
 * solver island 参数系统构建器。
 *
 * 设计说明：
 * 1. 把纯数据 island 快照（点局部坐标/边/关系）与尺寸领域快照编译为
 *    `SketchParametricSystem`（02 计划 §7.1 数据流的入口段）；
 * 2. 所有对象式关系均通过可注入公式注册表编译；尚未迁移的边界类型统一进入
 *    unsupported 报告，由上层放弃参数化写回，禁止忽略未知关系强行求解；
 * 3. 分支符号 branch.directionSign 优先；缺省时按当前几何推导并回报
 *    `resolvedDimensionBranches`，供上层写回尺寸聚合（02 计划 §7.5）。
 */
export class SketchParametricSystemBuilder {
    /** 已迁移关系族的公式注册表。 */
    private readonly constraintFormulaRegistry: SketchConstraintFormulaRegistry;
    /** 尺寸领域公式编译器。 */
    private readonly dimensionFormulaCompiler: SketchDimensionFormulaCompilerContract;
    /** 瞬态操作意图公式编译器。 */
    private readonly operationIntentCompiler: SketchParametricOperationIntentCompilerContract;

    /**
     * 创建 solver island 参数系统构建器。
     *
     * @param constraintFormulaRegistry 关系公式注册表；默认装配当前已迁移的全部关系公式编译器。
     * @param dimensionFormulaCompiler 尺寸公式编译器；默认装配统一尺寸公式实现。
     * @param operationIntentCompiler 操作意图编译器；默认装配统一瞬态输入公式实现。
     */
    public constructor(
        constraintFormulaRegistry: SketchConstraintFormulaRegistry = new SketchDefaultConstraintFormulaRegistryFactory()
            .create(),
        dimensionFormulaCompiler: SketchDimensionFormulaCompilerContract = new SketchDimensionFormulaCompiler(),
        operationIntentCompiler: SketchParametricOperationIntentCompilerContract
            = new SketchParametricOperationIntentCompiler(),
    ) {
        this.constraintFormulaRegistry = constraintFormulaRegistry;
        this.dimensionFormulaCompiler = dimensionFormulaCompiler;
        this.operationIntentCompiler = operationIntentCompiler;
    }

    /**
     * 构建 island 参数系统。
     *
     * @param input 构建输入。
     * @returns 构建结果。
     */
    public build(input: SketchParametricIslandBuildInput): SketchParametricIslandBuildResult {
        const island = input.island;
        const system = new SketchParametricSystem();
        const pointHandles = new Map<string, SketchParametricPointHandle>();
        const radiusParameterIds = new Map<string, string>();
        const dimensionEquationIds = new Map<string, string>();
        const resolvedDimensionBranches = new Map<string, 1 | -1>();
        const relationBranchHintReaders = new Map<string, () => SerializablePlainData>();
        const unsupportedConstraints: SketchParametricUnsupportedItem[] = [];
        const unsupportedDimensions: SketchParametricUnsupportedItem[] = [];
        const unsupportedOperationIntents: SketchParametricUnsupportedOperationIntent[] = [];
        const operationTargetPointIds = this.collectOperationTargetPointIds(input.operationIntents ?? []);
        const pointStateById = new Map(island.pointStates.map((pointState) => [pointState.pointId, pointState]));
        /**
         * 临时权威输入允许调用方同时传持久 ID 或运行时 ID。builder 在唯一参数系统入口
         * 完成身份归一化，避免 PropertyEdit、引用重算等上层各自复制一套固定规则。
         */
        const resolveRuntimeId = (candidateId: string): string => input.entityIdAliases?.get(candidateId)
            ?? candidateId;
        const authoritativePointIds = new Set(
            (input.authoritativePointIds ?? []).map(resolveRuntimeId),
        );
        const authoritativeEntityIds = new Set(
            (input.authoritativeEntityIds ?? []).map(resolveRuntimeId),
        );

        // Fix 关系与本轮权威输入先行：其目标点（含实体展开）注册为固定参数。
        const fixedPointIds = this.collectFixedPointIds(
            island,
            authoritativePointIds,
            authoritativeEntityIds,
        );
        // 抛物线直接拖拽只释放被选定义点；未选焦点/顶点保持为本轮交互锚点。
        const operationFixedPointIds = this.collectOperationFixedParabolaPointIds(
            island,
            operationTargetPointIds,
            pointStateById,
        );
        // 实体级 Fix 还必须冻结半径、样条尺度等非点基础参数。
        const fixedEntityIds = new Set([
            ...this.collectRelationTargetEntityIds(island, 'Fix'),
            ...authoritativeEntityIds,
        ]);
        // FixSlot 除成员点外还必须冻结半宽、中心弧半径和角度结构参数。
        const fixedSlotEntityIds = new Set([
            ...this.collectRelationTargetEntityIds(island, 'FixSlot'),
            ...authoritativeEntityIds,
        ]);

        island.pointStates.forEach((pointState) => {
            const fixed = Boolean(pointState.fixed)
                || fixedPointIds.has(pointState.pointId)
                || operationFixedPointIds.has(pointState.pointId);

            pointHandles.set(
                pointState.pointId,
                system.addPoint(pointState.pointId, pointState.localPosition[0], pointState.localPosition[1], fixed),
            );
        });

        /** 槽口 ID → 结构标量参数（相等槽口按此绑等式）。 */
        const slotStructureParameterIds = new Map<string, SketchParametricSlotStructureParameters>();
        /** 槽口 ID → 全部成员点 ID（固定槽口按此登记固定参数）。 */
        const slotMemberPointIds = new Map<string, readonly string[]>();
        const linePointHandles = new Map<string, readonly [SketchParametricPointHandle, SketchParametricPointHandle]>();
        const entityPointHandles = new Map<string, readonly SketchParametricPointHandle[]>();
        const centerPointHandles = new Map<string, SketchParametricPointHandle>();
        const arcGeometryStates = new Map<string, {
            readonly start: SketchParametricPointHandle;
            readonly end: SketchParametricPointHandle;
            readonly center: SketchParametricPointHandle;
            readonly radiusParameterId: string;
            readonly clockwise: boolean;
        }>();
        const ellipseGeometryStates = new Map<string, SketchParametricEllipseGeometryState>();
        const parabolaGeometryStates = new Map<string, SketchParametricParabolaGeometryState>();

        island.edgeStates.forEach((edgeState) => {
            const edgeKind = normalizeEdgeKind(edgeState.entityKind);

            if (edgeKind === 'line') {
                /**
                 * 采集侧已在 geometry 中声明有序起终点，必须优先使用该语义角色。
                 * 角度等有向关系依赖端点顺序；若只从排序点集按距离猜测，UUID 顺序变化
                 * 就会把锁定角翻转 π。旧快照没有角色时才回退到最远点对。
                 */
                const declaredStart = typeof edgeState.geometry.startPointId === 'string'
                    ? pointHandles.get(edgeState.geometry.startPointId)
                    : undefined;
                const declaredEnd = typeof edgeState.geometry.endPointId === 'string'
                    ? pointHandles.get(edgeState.geometry.endPointId)
                    : undefined;

                if (declaredStart && declaredEnd && declaredStart !== declaredEnd) {
                    linePointHandles.set(edgeState.entityId, [declaredStart, declaredEnd]);
                    return;
                }

                const candidates = edgeState.pointIds
                    .map((pointId) => ({
                        handle: pointHandles.get(pointId),
                        state: pointStateById.get(pointId),
                    }))
                    .filter((candidate): candidate is {
                        handle: SketchParametricPointHandle;
                        state: NonNullable<ReturnType<typeof pointStateById.get>>;
                    } => Boolean(candidate.handle && candidate.state));

                if (candidates.length < 2) {
                    return;
                }

                let bestPair: readonly [number, number] = [0, 1];
                let bestDistance = -1;

                for (let first = 0; first < candidates.length; first += 1) {
                    for (let second = first + 1; second < candidates.length; second += 1) {
                        const left = candidates[first]!.state.localPosition;
                        const right = candidates[second]!.state.localPosition;
                        const distance = Math.hypot(right[0] - left[0], right[1] - left[1]);

                        if (distance > bestDistance) {
                            bestDistance = distance;
                            bestPair = [first, second];
                        }
                    }
                }
                linePointHandles.set(edgeState.entityId, [
                    candidates[bestPair[0]]!.handle,
                    candidates[bestPair[1]]!.handle,
                ]);
                return;
            }
            if (edgeKind === 'ellipse') {
                /** 按快照点角色解析椭圆基准轴控制点，禁止根据点数组顺序猜测。 */
                const resolveRoleHandle = (role: string): SketchParametricPointHandle | undefined => {
                    const pointId = edgeState.pointIds.find((candidateId) => (
                        pointStateById.get(candidateId)?.role?.split('/').includes(role) === true
                    ));

                    return pointId ? pointHandles.get(pointId) : undefined;
                };
                const declaredCenterPointId = edgeState.geometry.centerPointId;
                const center = typeof declaredCenterPointId === 'string'
                    ? pointHandles.get(declaredCenterPointId)
                    : resolveRoleHandle('center');
                const majorPositive = resolveRoleHandle('majorPositive');
                const minorPositive = resolveRoleHandle('minorPositive');

                if (!center || !majorPositive || !minorPositive) {
                    return;
                }

                const majorNegative = resolveRoleHandle('majorNegative');
                const minorNegative = resolveRoleHandle('minorNegative');
                const cardinalHandles = new Set([
                    center,
                    majorPositive,
                    minorPositive,
                    majorNegative,
                    minorNegative,
                ].filter((handle): handle is SketchParametricPointHandle => Boolean(handle)));
                const declaredStart = typeof edgeState.geometry.startPointId === 'string'
                    ? pointHandles.get(edgeState.geometry.startPointId)
                    : undefined;
                const declaredEnd = typeof edgeState.geometry.endPointId === 'string'
                    ? pointHandles.get(edgeState.geometry.endPointId)
                    : undefined;
                const start = declaredStart && !cardinalHandles.has(declaredStart) ? declaredStart : undefined;
                const end = declaredEnd && !cardinalHandles.has(declaredEnd) ? declaredEnd : undefined;

                ellipseGeometryStates.set(edgeState.entityId, {
                    center,
                    majorPositive,
                    minorPositive,
                    ...(majorNegative ? {majorNegative} : {}),
                    ...(minorNegative ? {minorNegative} : {}),
                    ...(start ? {start} : {}),
                    ...(end ? {end} : {}),
                });
                // 长短轴必须保持正交；半轴长度退化由点在椭圆/相切公式显式判为 invalid。
                system.addEquation(SketchParametricEquationFactory.perpendicular(
                    `ellipse:${edgeState.entityId}:axis-perpendicular`,
                    center,
                    majorPositive,
                    center,
                    minorPositive,
                ));

                if (majorNegative) {
                    system.addEquation(SketchParametricEquationFactory.midpoint(
                        `ellipse:${edgeState.entityId}:major-symmetric`,
                        center,
                        majorPositive,
                        majorNegative,
                    ));
                }
                if (minorNegative) {
                    system.addEquation(SketchParametricEquationFactory.midpoint(
                        `ellipse:${edgeState.entityId}:minor-symmetric`,
                        center,
                        minorPositive,
                        minorNegative,
                    ));
                }

                /** 部分椭圆端点或完整椭圆边界点必须始终留在同一基础椭圆上。 */
                edgeState.pointIds.forEach((pointId, index) => {
                    const handle = pointHandles.get(pointId);

                    if (!handle || cardinalHandles.has(handle)) {
                        return;
                    }
                    system.addEquation(SketchParametricEquationFactory.pointOnEllipse(
                        `ellipse:${edgeState.entityId}:point-on-curve:${index}`,
                        handle,
                        center,
                        majorPositive,
                        minorPositive,
                    ));
                });
                return;
            }
            if (edgeKind === 'parabola') {
                /** 抛物线结构角色由采集侧写入点状态，公式层不依赖数组顺序。 */
                const resolveRolePointId = (role: string): string | undefined => (
                    edgeState.pointIds.find((candidateId) => (
                        pointStateById.get(candidateId)?.role?.split('/').includes(role) === true
                    ))
                );
                const focalPointId = resolveRolePointId('focal');
                const apexPointId = resolveRolePointId('apex');
                const startPointId = resolveRolePointId('start');
                const endPointId = resolveRolePointId('end');
                const focal = focalPointId ? pointHandles.get(focalPointId) : undefined;
                const apex = apexPointId ? pointHandles.get(apexPointId) : undefined;
                const start = startPointId ? pointHandles.get(startPointId) : undefined;
                const end = endPointId ? pointHandles.get(endPointId) : undefined;

                if (!focal || !apex) {
                    return;
                }

                const definition = edgeState.geometry.parabolaDefinition;
                const serializedStartParameter = definition && typeof definition === 'object'
                    ? Reflect.get(definition, 'startParameter')
                    : undefined;
                const serializedEndParameter = definition && typeof definition === 'object'
                    ? Reflect.get(definition, 'endParameter')
                    : undefined;
                const hasDirectOperationTarget = edgeState.pointIds.some((pointId) => (
                    operationTargetPointIds.has(pointId)
                ));
                const startParameter = start
                    ? this.resolveParabolaEndpointParameter(
                        system,
                        start,
                        focal,
                        apex,
                        serializedStartParameter,
                    )
                    : null;
                const endParameter = end
                    ? this.resolveParabolaEndpointParameter(
                        system,
                        end,
                        focal,
                        apex,
                        serializedEndParameter,
                    )
                    : null;
                const startParameterId = start && startParameter !== null
                    ? system.addParabolaEndpointParameter(
                        edgeState.entityId,
                        'start',
                        startParameter,
                        hasDirectOperationTarget && !operationTargetPointIds.has(startPointId ?? ''),
                    )
                    : undefined;
                const endParameterId = end && endParameter !== null
                    ? system.addParabolaEndpointParameter(
                        edgeState.entityId,
                        'end',
                        endParameter,
                        hasDirectOperationTarget && !operationTargetPointIds.has(endPointId ?? ''),
                    )
                    : undefined;

                parabolaGeometryStates.set(edgeState.entityId, {
                    focal,
                    apex,
                    ...(start ? {start} : {}),
                    ...(end ? {end} : {}),
                    ...(startParameterId ? {startParameterId} : {}),
                    ...(endParameterId ? {endParameterId} : {}),
                });
                if (start) {
                    system.addEquation(startParameterId
                        ? SketchParametricEquationFactory.pointAtParabolaParameter(
                            `parabola:${edgeState.entityId}:start-at-parameter`,
                            start,
                            focal,
                            apex,
                            startParameterId,
                        )
                        : SketchParametricEquationFactory.pointOnParabola(
                            `parabola:${edgeState.entityId}:start-on-curve`,
                            start,
                            focal,
                            apex,
                        ));
                }
                if (end) {
                    system.addEquation(endParameterId
                        ? SketchParametricEquationFactory.pointAtParabolaParameter(
                            `parabola:${edgeState.entityId}:end-at-parameter`,
                            end,
                            focal,
                            apex,
                            endParameterId,
                        )
                        : SketchParametricEquationFactory.pointOnParabola(
                            `parabola:${edgeState.entityId}:end-on-curve`,
                            end,
                            focal,
                            apex,
                        ));
                }
                return;
            }
            if (edgeKind !== 'circle' && edgeKind !== 'arc') {
                return;
            }

            // 半径所在字段按实体类型解析（圆在 radius、圆弧在 arcDefinition.radius），
            // 与写回侧共用同一份参数化契约，避免圆弧永远注册不到半径参数。
            const radius = resolveEntityRadiusFromGeometrySnapshot(edgeKind, edgeState.geometry);
            const radiusParameterId = radius !== null
                ? system.addRadiusParameter(edgeState.entityId, radius, fixedEntityIds.has(edgeState.entityId))
                : null;

            if (radiusParameterId) {
                radiusParameterIds.set(edgeState.entityId, radiusParameterId);
            }

            // 圆心点结构角色由快照声明（`pointIds` 无序、无法区分圆心与圆周点）。
            const centerPointId = edgeState.geometry.centerPointId;
            const centerHandle = typeof centerPointId === 'string' ? pointHandles.get(centerPointId) : undefined;

            if (centerHandle) {
                centerPointHandles.set(edgeState.entityId, centerHandle);
            }

            /** 弧的起/终点结构角色（快照声明）：路径总长弧段的测量与方程共用。 */
            if (edgeKind === 'arc' && centerHandle && radiusParameterId) {
                const startHandle = typeof edgeState.geometry.startPointId === 'string'
                    ? pointHandles.get(edgeState.geometry.startPointId)
                    : undefined;
                const endHandle = typeof edgeState.geometry.endPointId === 'string'
                    ? pointHandles.get(edgeState.geometry.endPointId)
                    : undefined;
                const arcDefinition = edgeState.geometry.arcDefinition as Record<string, unknown> | undefined;

                if (startHandle && endHandle) {
                    arcGeometryStates.set(edgeState.entityId, {
                        start: startHandle,
                        end: endHandle,
                        center: centerHandle,
                        radiusParameterId,
                        clockwise: arcDefinition?.clockwise === true,
                    });
                }
            }

            /**
             * 圆弧结构耦合：端点/中点声明为「到圆心距离 = 半径」。
             *
             * 圆弧端点常与相邻线段共享同一个点对象，而 `refreshGeometryOnly` 会把端点
             * 吸附到精确圆周——解不自洽时这次吸附会连带拖动邻接线段，造成尺寸静默错值。
             * 圆的圆周控制点虽然不与其它实体共享，但它会在轮廓平移、缩放和半径控制点
             * 拖拽时成为直接输入；这些点操作必须让同一关系岛内的全部圆声明结构方程，
             * 因为未被直接拖拽的圆也可能由对称等关系间接驱动。若只耦合直接命中的圆，
             * 传播侧会用旧派生控制点反解半径。半径属性输入和普通重算没有点目标，此时
             * 控制点仍是运行时派生量；若无条件把它耦合进正式硬约束与 Jacobian，会改变
             * 欠定义关系岛的最小范数解。
             *
             * 槽口端帽弧除外：槽口已按「轴 + 半宽」声明过全部成员的结构位置，
             * 再声明一次端帽半径会把同一物理量拆成两个参数、扰乱最小范数解的分配
             * （实测表现为驱动槽口长度时宽度一起漂移）。
             */
            const belongsToSlot = Boolean(edgeState.geometry.slotStructure);
            const requiresCirclePointStructure = edgeKind === 'circle'
                && operationTargetPointIds.size > 0;

            if ((edgeKind === 'arc' || requiresCirclePointStructure)
                && radiusParameterId && centerHandle && !belongsToSlot) {
                edgeState.pointIds
                    .filter((pointId) => pointId !== centerPointId)
                    .forEach((pointId, index) => {
                        const handle = pointHandles.get(pointId);

                        if (!handle) {
                            return;
                        }
                        system.addEquation(SketchParametricEquationFactory.pointOnCircle(
                            `${edgeKind}:${edgeState.entityId}:on-circle:${index}`,
                            handle,
                            centerHandle,
                            radiusParameterId,
                        ));
                    });
            }
        });

        /**
         * 把各实体适配为稳定、有序的定义点序列，供通用刚体阵列关系消费。
         * `edgeState.pointIds` 是 UUID 排序后的无序影响集，绝不能直接拿来做
         * 种子—实例点位配对；这里仅使用几何快照中的明确角色和有序控制点协议。
         */
        island.edgeStates.forEach((edgeState) => {
            const edgeKind = normalizeEdgeKind(edgeState.entityKind);
            const orderedPointIds: string[] = [];
            const appendPointId = (pointId: unknown): void => {
                if (typeof pointId === 'string' && pointId.length > 0 && !orderedPointIds.includes(pointId)) {
                    orderedPointIds.push(pointId);
                }
            };
            const appendRolePoint = (role: string): void => {
                appendPointId(edgeState.pointIds.find((pointId) => (
                    pointStateById.get(pointId)?.role?.split('/').includes(role) === true
                )));
            };

            if (edgeKind === 'line') {
                appendPointId(edgeState.geometry.startPointId);
                appendPointId(edgeState.geometry.endPointId);
            } else if (edgeKind === 'circle') {
                appendPointId(edgeState.geometry.centerPointId);
            } else if (edgeKind === 'arc') {
                appendPointId(edgeState.geometry.centerPointId);
                appendPointId(edgeState.geometry.startPointId);
                appendPointId(edgeState.geometry.endPointId);
            } else if (edgeKind === 'ellipse') {
                appendPointId(edgeState.geometry.centerPointId);
                appendRolePoint('majorPositive');
                appendRolePoint('minorPositive');
                appendRolePoint('majorNegative');
                appendRolePoint('minorNegative');
                appendPointId(edgeState.geometry.startPointId);
                appendPointId(edgeState.geometry.endPointId);
            } else if (edgeKind === 'parabola') {
                appendRolePoint('focal');
                appendRolePoint('apex');
                appendRolePoint('start');
                appendRolePoint('end');
            } else {
                const splineStructure = edgeState.geometry.splineStructure;
                const controlPointIds = splineStructure && typeof splineStructure === 'object'
                    ? Reflect.get(splineStructure, 'controlPointIds')
                    : null;
                if (Array.isArray(controlPointIds)) {
                    controlPointIds.forEach(appendPointId);
                }
            }

            const orderedHandles = orderedPointIds
                .map((pointId) => pointHandles.get(pointId))
                .filter((handle): handle is SketchParametricPointHandle => Boolean(handle));
            if (orderedHandles.length === orderedPointIds.length && orderedHandles.length > 0) {
                entityPointHandles.set(edgeState.entityId, orderedHandles);
            }
        });

        /**
         * 注册别名等价键：持久 ID 与运行时 uuid 指向同一句柄/参数，
         * 尺寸编译与测量按持久 ID 解析时无需感知身份空间差异。
         */
        input.entityIdAliases?.forEach((memberId, aliasId) => {
            const pointHandle = pointHandles.get(memberId);

            if (pointHandle && !pointHandles.has(aliasId)) {
                pointHandles.set(aliasId, pointHandle);
            }

            const lineHandles = linePointHandles.get(memberId);

            if (lineHandles && !linePointHandles.has(aliasId)) {
                linePointHandles.set(aliasId, lineHandles);
            }

            const entityHandles = entityPointHandles.get(memberId);

            if (entityHandles && !entityPointHandles.has(aliasId)) {
                entityPointHandles.set(aliasId, entityHandles);
            }

            const radiusParameterId = radiusParameterIds.get(memberId);

            if (radiusParameterId && !radiusParameterIds.has(aliasId)) {
                radiusParameterIds.set(aliasId, radiusParameterId);
            }

            const centerHandle = centerPointHandles.get(memberId);

            if (centerHandle && !centerPointHandles.has(aliasId)) {
                centerPointHandles.set(aliasId, centerHandle);
            }

            const arcGeometry = arcGeometryStates.get(memberId);

            if (arcGeometry && !arcGeometryStates.has(aliasId)) {
                arcGeometryStates.set(aliasId, arcGeometry);
            }

            const ellipseGeometry = ellipseGeometryStates.get(memberId);

            if (ellipseGeometry && !ellipseGeometryStates.has(aliasId)) {
                ellipseGeometryStates.set(aliasId, ellipseGeometry);
            }

            const parabolaGeometry = parabolaGeometryStates.get(memberId);

            if (parabolaGeometry && !parabolaGeometryStates.has(aliasId)) {
                parabolaGeometryStates.set(aliasId, parabolaGeometry);
            }
        });

        /**
         * 直槽口结构耦合：槽口不是边对象、不在 edgeStates，结构信息挂在成员边几何快照上，
         * 这里按 slotId 去重后声明其真实自由度（轴复用中心线起止点 + 新增半宽参数），
         * 并把接合点/端帽圆心钉到结构位置。缺任一句柄时整槽跳过，宁可不约束也不产出半截系统。
         */
        const compiledSlotIds = new Set<string>();

        island.edgeStates.forEach((edgeState) => {
            const structure = edgeState.geometry.slotStructure as Record<string, unknown> | undefined;

            if (!structure || typeof structure !== 'object') {
                return;
            }

            const slotId = String(structure.slotId ?? '');
            const junctionPointIds = structure.junctionPointIds;

            if (slotId.length === 0 || compiledSlotIds.has(slotId) || !Array.isArray(junctionPointIds)) {
                return;
            }

            const axisStart = pointHandles.get(String(structure.axisStartPointId ?? ''));
            const axisEnd = pointHandles.get(String(structure.axisEndPointId ?? ''));
            const halfWidth = structure.halfWidth;
            const normalSign = structure.normalSign;

            if (!axisStart || !axisEnd || typeof halfWidth !== 'number' || !Number.isFinite(halfWidth)
                || (normalSign !== 1 && normalSign !== -1)) {
                return;
            }

            /** 从动点绑定表：[点 ID, 沿轴插值比, 法向偏移符号]。 */
            const bindings: [unknown, number, number][] = [
                [junctionPointIds[0], 0, normalSign],
                [junctionPointIds[1], 1, normalSign],
                [junctionPointIds[2], 1, -normalSign],
                [junctionPointIds[3], 0, -normalSign],
                [structure.startCapCenterPointId, 0, 0],
                [structure.endCapCenterPointId, 1, 0],
                // 中心控制点 = 轴中点；不声明它就是自由点，会被留在原地并累积偏差。
                [structure.centerPointId, 0.5, 0],
            ];
            const resolved = bindings
                .filter(([pointId]) => String(pointId ?? '').length > 0)
                .map(([pointId, alongRatio, offsetSign]) => ({
                    handle: pointHandles.get(String(pointId ?? '')),
                    alongRatio,
                    offsetSign,
                }));

            if (resolved.some((binding) => !binding.handle)) {
                return;
            }

            const halfWidthParameterId = system.addSlotHalfWidthParameter(
                slotId,
                halfWidth,
                fixedSlotEntityIds.has(slotId),
            );

            resolved.forEach((binding, index) => {
                system.addEquation(SketchParametricEquationFactory.slotStructureBinding(
                    `slot:${slotId}:binding:${index}`,
                    axisStart,
                    axisEnd,
                    halfWidthParameterId,
                    binding.handle!,
                    binding.alongRatio,
                    binding.offsetSign,
                ));
            });
            /**
             * 端帽弧半径 = 槽口半宽（同一物理量的两个参数必须绑等式）。
             *
             * 不绑的后果：半径参数在求解里完全自由、停在旧值，写回阶段
             * `applyEntityParameters` 按旧半径 `syncControlPointsFromDefinition`，
             * 把刚按新半宽摆好的接合点又吸附回去 —— 表现为"改宽度尺寸弹回原值"。
             */
            [structure.startCapArcId, structure.endCapArcId].forEach((capArcId, index) => {
                const capRadiusParameterId = radiusParameterIds.get(String(capArcId ?? ''));

                if (!capRadiusParameterId) {
                    return;
                }
                system.addEquation(SketchParametricEquationFactory.scalarEqual(
                    `slot:${slotId}:cap-radius:${index}`,
                    capRadiusParameterId,
                    halfWidthParameterId,
                ));
            });
            slotStructureParameterIds.set(slotId, {halfWidthParameterId, axisHandles: [axisStart, axisEnd]});
            slotMemberPointIds.set(slotId, [
                String(structure.axisStartPointId ?? ''),
                String(structure.axisEndPointId ?? ''),
                String(structure.startCapCenterPointId ?? ''),
                String(structure.endCapCenterPointId ?? ''),
                String(structure.centerPointId ?? ''),
                ...junctionPointIds.map((pointId) => String(pointId ?? '')),
            ]);
            compiledSlotIds.add(slotId);
        });

        /**
         * 圆弧槽口结构耦合：真实自由度 = 弧心(2) + 中心弧半径 R + 半宽 h + 起止角 θs/θe，
         * 其余成员（4 接合点 / 2 端帽圆心 / 中心弧中点 / 内外弧与端帽弧半径）全是派生量。
         *
         * 不声明的后果与直槽口同类：求解器只会挪尺寸直接引用的那两个点，
         * 槽口其余成员原地不动 —— 控制点与几何脱节、拖任一成员即散架。
         */
        const compiledArcSlotIds = new Set<string>();

        island.edgeStates.forEach((edgeState) => {
            const structure = edgeState.geometry.arcSlotStructure as Record<string, unknown> | undefined;

            if (!structure || typeof structure !== 'object') {
                return;
            }

            const slotId = String(structure.slotId ?? '');
            const junctionPointIds = structure.junctionPointIds;

            if (slotId.length === 0 || compiledArcSlotIds.has(slotId) || !Array.isArray(junctionPointIds)) {
                return;
            }

            const center = pointHandles.get(String(structure.arcCenterPointId ?? ''));
            const radius = structure.radius;
            const halfWidth = structure.halfWidth;
            const startAngle = structure.startAngle;
            const endAngle = structure.endAngle;

            if (!center
                || typeof radius !== 'number' || !Number.isFinite(radius)
                || typeof halfWidth !== 'number' || !Number.isFinite(halfWidth)
                || typeof startAngle !== 'number' || !Number.isFinite(startAngle)
                || typeof endAngle !== 'number' || !Number.isFinite(endAngle)) {
                return;
            }

            /** 派生点绑定表：[点 ID, 法向偏移符号, 用哪个角]。 */
            const pointBindings: [unknown, number, 'start' | 'end'][] = [
                [junctionPointIds[0], 1, 'start'],
                [junctionPointIds[1], 1, 'end'],
                [junctionPointIds[2], -1, 'end'],
                [junctionPointIds[3], -1, 'start'],
                [structure.startCapCenterPointId, 0, 'start'],
                [structure.endCapCenterPointId, 0, 'end'],
            ];
            const resolvedPoints = pointBindings.map(([pointId, offsetSign, angleRole]) => ({
                handle: pointHandles.get(String(pointId ?? '')),
                offsetSign,
                angleRole,
            }));
            const midpoint = pointHandles.get(String(structure.centerArcMidpointId ?? ''));

            // 缺任一句柄时整槽跳过：宁可不声明，也不产出半截结构。
            if (!midpoint || resolvedPoints.some((binding) => !binding.handle)) {
                return;
            }

            /**
             * 角度参数按**等效弧长**注册（θ×种子半径）：最小范数解在欧氏度量下比较各参数的
             * 变化量，弧度与米混用会让 LM 优先改半径而不是改角度（实测半径被拽塌）。
             */
            const angleScale = radius;
            const slotFixed = fixedSlotEntityIds.has(slotId);
            const radiusParameterId = system.addArcSlotStructureParameter(slotId, 'radius', radius, slotFixed);
            const halfWidthParameterId = system.addArcSlotStructureParameter(
                slotId,
                'half-width',
                halfWidth,
                slotFixed,
            );
            const startAngleParameterId = system.addArcSlotStructureParameter(
                slotId,
                'start-angle',
                startAngle * angleScale,
                slotFixed,
            );
            const endAngleParameterId = system.addArcSlotStructureParameter(
                slotId,
                'end-angle',
                endAngle * angleScale,
                slotFixed,
            );

            resolvedPoints.forEach((binding, index) => {
                system.addEquation(SketchParametricEquationFactory.arcSlotPolarBinding(
                    `arc-slot:${slotId}:binding:${index}`,
                    center,
                    binding.handle!,
                    radiusParameterId,
                    halfWidthParameterId,
                    binding.angleRole === 'start' ? startAngleParameterId : endAngleParameterId,
                    binding.offsetSign,
                    angleScale,
                ));
            });
            system.addEquation(SketchParametricEquationFactory.arcSlotMidpointBinding(
                `arc-slot:${slotId}:midpoint`,
                center,
                midpoint,
                radiusParameterId,
                startAngleParameterId,
                endAngleParameterId,
                angleScale,
            ));

            /** 成员弧半径派生：外弧 = R + h、内弧 = R − h、两端帽 = h。 */
            const radiusBindings: [unknown, number][] = [
                [structure.outerArcId, 1],
                [structure.innerArcId, -1],
            ];

            radiusBindings.forEach(([arcId, offsetSign], index) => {
                const memberRadiusParameterId = radiusParameterIds.get(String(arcId ?? ''));

                if (!memberRadiusParameterId) {
                    return;
                }
                system.addEquation(SketchParametricEquationFactory.scalarOffsetEqual(
                    `arc-slot:${slotId}:member-radius:${index}`,
                    memberRadiusParameterId,
                    radiusParameterId,
                    halfWidthParameterId,
                    offsetSign,
                ));
            });
            [structure.startCapArcId, structure.endCapArcId].forEach((capArcId, index) => {
                const capRadiusParameterId = radiusParameterIds.get(String(capArcId ?? ''));

                if (!capRadiusParameterId) {
                    return;
                }
                system.addEquation(SketchParametricEquationFactory.scalarEqual(
                    `arc-slot:${slotId}:cap-radius:${index}`,
                    capRadiusParameterId,
                    halfWidthParameterId,
                ));
            });
            /**
             * 槽口 ID 也登记为半径参数宿主：圆弧槽口的中心弧没有独立实体，
             * 半径尺寸直接锚在槽口上，编译时按同一张表解析（别名机制再补持久 ID 键）。
             */
            radiusParameterIds.set(slotId, radiusParameterId);
            input.entityIdAliases?.forEach((memberId, aliasId) => {
                if (memberId === slotId && !radiusParameterIds.has(aliasId)) {
                    radiusParameterIds.set(aliasId, radiusParameterId);
                }
            });
            slotStructureParameterIds.set(slotId, {
                halfWidthParameterId,
                radiusParameterId,
                startAngleParameterId,
                endAngleParameterId,
                angleScale,
                polarAnchorHandles: [
                    center,
                    pointHandles.get(String(structure.startCapCenterPointId ?? '')),
                    pointHandles.get(String(structure.endCapCenterPointId ?? '')),
                ].filter((handle): handle is SketchParametricPointHandle => Boolean(handle)),
            });
            slotMemberPointIds.set(slotId, [
                String(structure.arcCenterPointId ?? ''),
                String(structure.centerArcMidpointId ?? ''),
                String(structure.startCapCenterPointId ?? ''),
                String(structure.endCapCenterPointId ?? ''),
                ...junctionPointIds.map((pointId) => String(pointId ?? '')),
            ]);
            compiledArcSlotIds.add(slotId);
        });

        /**
         * 样条参数化：精确曲线求值描述符与总长尺度参数分层建立。
         *
         * Bezier / 显式 B 样条直接复用当前控制点参数、权重和节点精确求值；总长尺寸或
         * EqualCurveLength 仍使用「锚点平移 + 整体缩放」，因为弧长对相似变换一次齐次。
         * 开放插值样条的极点由 Puppy CAD 内核计算，尚不能随插值点同步重建，因此暂不伪装
         * 为可变精确曲线描述符。
         */
        const splineLengthStates = new Map<string, SketchParametricSplineLengthState>();
        const splineCurveDescriptors = new Map<string, SketchParametricSplineCurveDescriptor>();
        const splineMirrorStructures = new Map<string, SketchParametricSplineMirrorStructure>();
        const splineLengthTargetIds = new Set<string>();

        (input.dimensions ?? []).forEach((dimension) => {
            if (dimension.dimensionKind !== 'spline-length'
                || dimension.role !== 'driving'
                || dimension.suppressed
                || dimension.solveState === 'dangling') {
                return;
            }

            const entityId = dimension.references[0]?.entityId;

            if (entityId) {
                splineLengthTargetIds.add(entityId);
            }
        });
        island.constraintStates.forEach((constraintState) => {
            if (constraintState.relationKind !== 'EqualCurveLength') {
                return;
            }

            const targetEntityIds = constraintState.targetRefs
                ?.filter((targetRef) => targetRef.targetKind === 'entity')
                .map((targetRef) => targetRef.targetId)
                ?? constraintState.targetEntityIds;

            targetEntityIds.forEach((entityId) => splineLengthTargetIds.add(entityId));
        });

        island.edgeStates.forEach((edgeState) => {
            const structure = edgeState.geometry.splineStructure as Record<string, unknown> | undefined;
            const requiresLengthParameterization = splineLengthTargetIds.has(edgeState.entityId)
                || [...(input.entityIdAliases ?? new Map())]
                    .some(([aliasId, memberId]) => memberId === edgeState.entityId && splineLengthTargetIds.has(aliasId));

            if (!structure || typeof structure !== 'object') {
                return;
            }

            const controlPointIds = structure.controlPointIds;
            const baseLength = structure.sampledLength;

            if (!Array.isArray(controlPointIds) || controlPointIds.length < 2
                || typeof baseLength !== 'number' || !Number.isFinite(baseLength) || baseLength <= 0) {
                return;
            }

            const handles = controlPointIds
                .map((pointId) => pointHandles.get(String(pointId ?? '')))
                .filter((handle): handle is SketchParametricPointHandle => Boolean(handle));
            const anchor = handles[0];

            // 缺任一控制点句柄时整条样条跳过：宁可不声明，也不产出半截结构。
            if (!anchor || handles.length !== controlPointIds.length) {
                return;
            }

            const variant = structure.variant;
            if (variant === 'standard' || variant === 'style') {
                splineMirrorStructures.set(edgeState.entityId, {
                    variant,
                    controlPoints: handles,
                });
            }

            const metadata = structure.curveMetadata as Record<string, unknown> | undefined;
            const curveKind = metadata?.curveKind;

            if (curveKind === 'bezier' || curveKind === 'bspline') {
                const rawWeights = Array.isArray(metadata.weights) ? metadata.weights : [];
                const weights = handles.map((_handle, index) => {
                    const weight = rawWeights[index];

                    return typeof weight === 'number' && Number.isFinite(weight) && weight > 0 ? weight : 1;
                });
                const degree = curveKind === 'bezier'
                    ? handles.length - 1
                    : Math.max(1, Math.min(
                        Math.floor(typeof metadata.degree === 'number' ? metadata.degree : 3),
                        handles.length - 1,
                    ));

                // Bezier/B-spline 的编辑点就是曲线极点，显式记录可让外部引用编译器保持同一编辑语义。
                splineCurveDescriptors.set(edgeState.entityId, {
                    kind: curveKind,
                    controlPoints: handles,
                    editPoints: handles,
                    weights,
                    degree,
                    knots: curveKind === 'bspline'
                        ? SketchParametricSplineEvaluator.buildClampedUniformKnots(handles.length, degree)
                        : [],
                    firstParameter: 0,
                    lastParameter: 1,
                });
            } else if (curveKind === 'spline') {
                const evaluation = structure.curveEvaluationDefinition as Record<string, unknown> | undefined;
                const poles = evaluation?.controlPoints;
                const rawWeights = evaluation?.weights;
                const rawKnots = evaluation?.knots;
                const rawDegree = evaluation?.degree;
                const firstParameter = evaluation?.firstParameter;
                const lastParameter = evaluation?.lastParameter;

                if (Array.isArray(poles) && poles.length >= 2
                    && Array.isArray(rawWeights) && rawWeights.length === poles.length
                    && Array.isArray(rawKnots)
                    && typeof rawDegree === 'number' && Number.isFinite(rawDegree)
                    && typeof firstParameter === 'number' && Number.isFinite(firstParameter)
                    && typeof lastParameter === 'number' && Number.isFinite(lastParameter)
                    && lastParameter > firstParameter) {
                    const poleHandles = poles.map((pole, index) => {
                        const x = pole && typeof pole === 'object' ? Reflect.get(pole, 'x') : undefined;
                        const y = pole && typeof pole === 'object' ? Reflect.get(pole, 'y') : undefined;

                        return typeof x === 'number' && Number.isFinite(x)
                            && typeof y === 'number' && Number.isFinite(y)
                            ? system.addPoint(`spline:${edgeState.entityId}:evaluation-pole:${index}`, x, y, true)
                            : null;
                    });
                    const weights = rawWeights.map((weight) => (
                        typeof weight === 'number' && Number.isFinite(weight) && weight > 0 ? weight : 1
                    ));
                    const knots = rawKnots
                        .filter((knot): knot is number => typeof knot === 'number' && Number.isFinite(knot));

                    if (poleHandles.every((handle) => handle !== null) && knots.length === rawKnots.length) {
                        // 普通插值样条的用户拟合点与内核极点不同，两组句柄必须分开。
                        splineCurveDescriptors.set(edgeState.entityId, {
                            kind: 'bspline',
                            controlPoints: poleHandles as SketchParametricPointHandle[],
                            editPoints: handles,
                            weights,
                            degree: Math.max(1, Math.min(Math.floor(rawDegree), poleHandles.length - 1)),
                            knots,
                            firstParameter,
                            lastParameter,
                        });
                    }
                }
            }

            if (!requiresLengthParameterization) {
                return;
            }

            const anchorSeed = pointStateById.get(String(controlPointIds[0]))?.localPosition;

            if (!anchorSeed) {
                return;
            }

            const scaleParameterId = system.addSplineScaleParameter(
                edgeState.entityId,
                1,
                fixedEntityIds.has(edgeState.entityId),
            );

            handles.forEach((handle, index) => {
                const seed = pointStateById.get(String(controlPointIds[index]))?.localPosition;

                if (index === 0 || !seed) {
                    return;
                }
                system.addEquation(SketchParametricEquationFactory.splineScaleBinding(
                    `spline:${edgeState.entityId}:binding:${index}`,
                    anchor,
                    handle,
                    scaleParameterId,
                    seed[0] - anchorSeed[0],
                    seed[1] - anchorSeed[1],
                ));
            });
            splineLengthStates.set(edgeState.entityId, {scaleParameterId, baseLength});
        });

        // 样条的尺寸引用是持久 ID：按别名补齐等价键，尺寸编译与测量无需感知身份空间。
        input.entityIdAliases?.forEach((memberId, aliasId) => {
            const state = splineLengthStates.get(memberId);

            if (state && !splineLengthStates.has(aliasId)) {
                splineLengthStates.set(aliasId, state);
            }

            const curveDescriptor = splineCurveDescriptors.get(memberId);

            if (curveDescriptor && !splineCurveDescriptors.has(aliasId)) {
                splineCurveDescriptors.set(aliasId, curveDescriptor);
            }

            const mirrorStructure = splineMirrorStructures.get(memberId);

            if (mirrorStructure && !splineMirrorStructures.has(aliasId)) {
                splineMirrorStructures.set(aliasId, mirrorStructure);
            }
        });

        /**
         * 统一曲线长度描述符。
         *
         * 关系编译器只消费该 plain data 映射，不再根据实体类型自行猜测“长度”等于半径、
         * 控制点距离或显示折线长度。别名已在各基础参数映射中补齐，因此持久 ID 与运行时 ID
         * 都能解析到同一组参数。
         */
        const curveLengthSegments = new Map<string, SketchParametricPathSegment>();

        linePointHandles.forEach((line, entityId) => {
            curveLengthSegments.set(entityId, {kind: 'line', start: line[0], end: line[1]});
        });
        radiusParameterIds.forEach((radiusParameterId, entityId) => {
            const arcState = arcGeometryStates.get(entityId);

            curveLengthSegments.set(entityId, arcState
                ? {
                    kind: 'arc',
                    start: arcState.start,
                    end: arcState.end,
                    center: arcState.center,
                    radiusParameterId,
                    clockwise: arcState.clockwise,
                }
                : {kind: 'circle', radiusParameterId});
        });
        splineLengthStates.forEach((state, entityId) => {
            curveLengthSegments.set(entityId, {
                kind: 'spline',
                baseLength: state.baseLength,
                scaleParameterId: state.scaleParameterId,
            });
        });

        /** 圆系与椭圆统一为隐式曲线，供两两接触和相切公式复用。 */
        const implicitCurveDescriptors = new Map<string, SketchParametricImplicitCurveDescriptor>();

        radiusParameterIds.forEach((radiusParameterId, entityId) => {
            const center = centerPointHandles.get(entityId);

            if (center) {
                implicitCurveDescriptors.set(entityId, {kind: 'circle', center, radiusParameterId});
            }
        });
        ellipseGeometryStates.forEach((ellipse, entityId) => {
            implicitCurveDescriptors.set(entityId, {
                kind: 'ellipse',
                center: ellipse.center,
                majorPositive: ellipse.majorPositive,
                minorPositive: ellipse.minorPositive,
            });
        });

        island.constraintStates.forEach((constraintState) => {
            this.compileConstraint({
                system,
                constraintState,
                pointHandles,
                linePointHandles,
                entityPointHandles,
                centerPointHandles,
                radiusParameterIds,
                arcGeometryStates,
                curveLengthSegments,
                ellipseGeometryStates,
                implicitCurveDescriptors,
                splineCurveDescriptors,
                splineMirrorStructures,
                parabolaGeometryStates,
                slotStructureParameterIds,
                slotMemberPointIds,
                fixedPointIds,
                fixedEntityIds,
                fixedSlotEntityIds,
                unsupportedConstraints,
                relationBranchHintReaders,
            });
        });

        /** 边状态查找（entityId + 别名等价键）：路径总长弧段解析结构角色用。 */
        const edgeStateByAnyId = new Map(island.edgeStates.map((edgeState) => [edgeState.entityId, edgeState]));

        input.entityIdAliases?.forEach((memberId, aliasId) => {
            const edgeState = edgeStateByAnyId.get(memberId);

            if (edgeState && !edgeStateByAnyId.has(aliasId)) {
                edgeStateByAnyId.set(aliasId, edgeState);
            }
        });

        (input.dimensions ?? []).forEach((dimension) => {
            this.dimensionFormulaCompiler.compile({
                system,
                dimension,
                pointHandles,
                linePointHandles,
                centerPointHandles,
                radiusParameterIds,
                slotStructureParameterIds,
                splineLengthStates,
                ellipseGeometryStates,
                parabolaGeometryStates,
                edgeStateByAnyId,
                dimensionEquationIds,
                resolvedDimensionBranches,
                unsupportedDimensions,
            });
        });

        (input.operationIntents ?? []).forEach((intent) => {
            this.operationIntentCompiler.compile({
                system,
                intent,
                pointHandles,
                linePointHandles,
                radiusParameterIds,
                unsupportedIntents: unsupportedOperationIntents,
            });
        });

        return {
            system,
            pointHandles,
            linePointHandles,
            radiusParameterIds,
            splineLengthStates,
            arcGeometryStates,
            ellipseGeometryStates,
            parabolaGeometryStates,
            dimensionEquationIds,
            resolvedDimensionBranches,
            relationBranchHintReaders,
            unsupportedConstraints,
            unsupportedDimensions,
            unsupportedOperationIntents,
        };
    }

    /**
     * 收集 Fix / FixSlot 关系锁定的点 ID（实体目标展开为其关联点）。
     *
     * `固定槽口`按 SW 语义锁定槽口的尺寸与位置，等价于把槽口全部成员点固定；
     * 槽口不是边对象、不在 `edgeStates` 里，因此按成员边几何快照上的结构声明展开。
     *
     * 临时权威点/实体只影响当前求解，不写入关系、历史或持久化；实体权威会展开为其
     * 全部控制点，槽口则展开为结构成员点，确保实体几何自由度不会被关系反向改写。
     *
     * @param island island 快照。
     * @param authoritativePointIds 本轮权威点 ID。
     * @param authoritativeEntityIds 本轮权威实体 ID。
     * @returns 固定点 ID 集合。
     */
    private collectFixedPointIds(
        island: SketchSolverIslandSnapshot,
        authoritativePointIds: ReadonlySet<string>,
        authoritativeEntityIds: ReadonlySet<string>,
    ): Set<string> {
        const fixedPointIds = new Set<string>();
        const edgeByEntityId = new Map(island.edgeStates.map((edgeState) => [edgeState.entityId, edgeState]));
        /** 槽口 ID → 成员点集合（直槽口与圆弧槽口的结构声明都在成员边上）。 */
        const slotPointIdsBySlotId = new Map<string, string[]>();

        island.edgeStates.forEach((edgeState) => {
            [edgeState.geometry.slotStructure, edgeState.geometry.arcSlotStructure].forEach((raw) => {
                const structure = raw as Record<string, unknown> | undefined;

                if (!structure || typeof structure !== 'object') {
                    return;
                }

                const slotId = String(structure.slotId ?? '');
                const junctionPointIds = Array.isArray(structure.junctionPointIds)
                    ? structure.junctionPointIds
                    : [];

                if (slotId.length === 0) {
                    return;
                }
                slotPointIdsBySlotId.set(slotId, [
                    ...junctionPointIds.map((pointId) => String(pointId ?? '')),
                    String(structure.axisStartPointId ?? ''),
                    String(structure.axisEndPointId ?? ''),
                    String(structure.arcCenterPointId ?? ''),
                    String(structure.centerArcMidpointId ?? ''),
                    String(structure.startCapCenterPointId ?? ''),
                    String(structure.endCapCenterPointId ?? ''),
                    String(structure.centerPointId ?? ''),
                ].filter((pointId) => pointId.length > 0));
            });
        });

        island.constraintStates.forEach((constraintState) => {
            if (constraintState.relationKind !== 'FixSlot') {
                return;
            }
            constraintState.targetEntityIds.forEach((entityId) => {
                slotPointIdsBySlotId.get(entityId)?.forEach((pointId) => fixedPointIds.add(pointId));
            });
        });

        island.constraintStates.forEach((constraintState) => {
            if (constraintState.relationKind !== 'Fix') {
                return;
            }
            constraintState.targetPointIds.forEach((pointId) => fixedPointIds.add(pointId));
            constraintState.targetEntityIds.forEach((entityId) => {
                edgeByEntityId.get(entityId)?.pointIds.forEach((pointId) => fixedPointIds.add(pointId));
            });
        });

        authoritativePointIds.forEach((pointId) => fixedPointIds.add(pointId));
        authoritativeEntityIds.forEach((entityId) => {
            edgeByEntityId.get(entityId)?.pointIds.forEach((pointId) => fixedPointIds.add(pointId));
            slotPointIdsBySlotId.get(entityId)?.forEach((pointId) => fixedPointIds.add(pointId));
        });

        return fixedPointIds;
    }

    /**
     * 收集指定结构关系直接目标中的实体 ID。
     *
     * @param island solver island 快照。
     * @param relationKind 需要收集的关系类型。
     * @returns 目标实体 ID 集合。
     */
    private collectRelationTargetEntityIds(
        island: SketchSolverIslandSnapshot,
        relationKind: 'Fix' | 'FixSlot',
    ): ReadonlySet<string> {
        const entityIds = new Set<string>();

        island.constraintStates.forEach((constraintState) => {
            if (constraintState.relationKind !== relationKind) {
                return;
            }

            const directEntityIds = constraintState.targetRefs
                ?.filter((targetRef) => targetRef.targetKind === 'entity')
                .map((targetRef) => targetRef.targetId)
                ?? constraintState.targetEntityIds;

            directEntityIds.forEach((entityId) => entityIds.add(entityId));
        });

        return entityIds;
    }

    /**
     * 收集本次瞬态操作直接指定的全部点 ID。
     *
     * 这些 ID 决定抛物线端点曲线参数是否允许变化：直接拖动端点时释放对应参数，
     * 拖动焦点、顶点或同一实体其它控制点时保持未选端点的可见段参数。
     *
     * @param intents 本次求解的操作意图集合。
     * @returns 去重后的直接目标点 ID。
     */
    private collectOperationTargetPointIds(
        intents: NonNullable<SketchParametricIslandBuildInput['operationIntents']>,
    ): ReadonlySet<string> {
        const pointIds = new Set<string>();

        intents.forEach((intent) => {
            if (intent.kind === 'entity-radius-target'
                || intent.kind === 'entity-line-length-target'
                || intent.isDirectManipulation === false) {
                return;
            }

            if (intent.kind === 'point-target') {
                pointIds.add(intent.pointId);
                return;
            }

            intent.targets.forEach((target) => pointIds.add(target.pointId));
        });

        return pointIds;
    }

    /**
     * 收集抛物线直接操作期间应保持不动的基础定义点。
     *
     * 抛物线的焦点和顶点共同定义局部轴与焦距。用户拖动其中一个控制点时，另一个
     * 必须作为交互锚点保持；拖动端点时两者都保持，端点只通过自己的曲线参数滑动。
     * 起终点坐标不能直接固定，因为焦点或顶点变化时它们需要由参数方程派生新位置。
     * 多点轮廓操作显式包含焦点和顶点时，两者均按操作目标释放。
     *
     * @param island solver island 快照。
     * @param operationTargetPointIds 本次直接操作目标点 ID。
     * @param pointStateById 点状态索引。
     * @returns 本轮需要额外固定的焦点/顶点 ID。
     */
    private collectOperationFixedParabolaPointIds(
        island: SketchSolverIslandSnapshot,
        operationTargetPointIds: ReadonlySet<string>,
        pointStateById: ReadonlyMap<string, SketchSolverIslandSnapshot['pointStates'][number]>,
    ): ReadonlySet<string> {
        const fixedPointIds = new Set<string>();

        if (operationTargetPointIds.size === 0) {
            return fixedPointIds;
        }

        island.edgeStates.forEach((edgeState) => {
            if (normalizeEdgeKind(edgeState.entityKind) !== 'parabola'
                || !edgeState.pointIds.some((pointId) => operationTargetPointIds.has(pointId))) {
                return;
            }

            edgeState.pointIds.forEach((pointId) => {
                if (operationTargetPointIds.has(pointId)) {
                    return;
                }

                const roles = pointStateById.get(pointId)?.role?.split('/') ?? [];
                if (roles.includes('focal') || roles.includes('apex')) {
                    fixedPointIds.add(pointId);
                }
            });
        });

        return fixedPointIds;
    }

    /**
     * 解析抛物线端点的侧向长度参数。
     *
     * 运行时实体快照中的 `startParameter/endParameter` 是权威定义；旧快照缺少该字段时，
     * 按当前焦点—顶点局部框架从端点坐标反算，确保历史数据仍能进入参数系统。
     * 侧向方向与 `SketchParabolaObject` 一致，使用局部二维中的 `axis × planeNormal`。
     *
     * @param system 当前参数系统。
     * @param point 端点参数句柄。
     * @param focal 焦点参数句柄。
     * @param apex 顶点参数句柄。
     * @param serializedValue 快照携带的实体定义参数。
     * @returns 合法长度参数；焦点和顶点退化时返回 null。
     */
    private resolveParabolaEndpointParameter(
        system: SketchParametricSystem,
        point: SketchParametricPointHandle,
        focal: SketchParametricPointHandle,
        apex: SketchParametricPointHandle,
        serializedValue: unknown,
    ): number | null {
        if (typeof serializedValue === 'number' && Number.isFinite(serializedValue)) {
            return serializedValue;
        }

        const axisX = system.read(focal.xId) - system.read(apex.xId);
        const axisY = system.read(focal.yId) - system.read(apex.yId);
        const focalLength = Math.hypot(axisX, axisY);

        if (focalLength <= Number.EPSILON) {
            return null;
        }

        const unitSideX = axisY / focalLength;
        const unitSideY = -axisX / focalLength;
        const pointOffsetX = system.read(point.xId) - system.read(apex.xId);
        const pointOffsetY = system.read(point.yId) - system.read(apex.yId);

        return pointOffsetX * unitSideX + pointOffsetY * unitSideY;
    }

    /** 编译单条几何关系为方程。 */
    private compileConstraint(input: {
        readonly system: SketchParametricSystem;
        readonly constraintState: SketchConstraintState;
        readonly pointHandles: ReadonlyMap<string, SketchParametricPointHandle>;
        readonly linePointHandles: ReadonlyMap<string, readonly [SketchParametricPointHandle, SketchParametricPointHandle]>;
        readonly entityPointHandles: ReadonlyMap<string, readonly SketchParametricPointHandle[]>;
        readonly centerPointHandles: ReadonlyMap<string, SketchParametricPointHandle>;
        readonly radiusParameterIds: ReadonlyMap<string, string>;
        readonly arcGeometryStates: SketchParametricIslandBuildResult['arcGeometryStates'];
        readonly curveLengthSegments: ReadonlyMap<string, SketchParametricPathSegment>;
        readonly ellipseGeometryStates: ReadonlyMap<string, SketchParametricEllipseGeometryState>;
        readonly implicitCurveDescriptors: ReadonlyMap<string, SketchParametricImplicitCurveDescriptor>;
        readonly splineCurveDescriptors: ReadonlyMap<string, SketchParametricSplineCurveDescriptor>;
        readonly splineMirrorStructures: ReadonlyMap<string, SketchParametricSplineMirrorStructure>;
        readonly parabolaGeometryStates: ReadonlyMap<string, SketchParametricParabolaGeometryState>;
        readonly slotStructureParameterIds: ReadonlyMap<string, SketchParametricSlotStructureParameters>;
        readonly slotMemberPointIds: ReadonlyMap<string, readonly string[]>;
        readonly fixedPointIds: ReadonlySet<string>;
        readonly fixedEntityIds: ReadonlySet<string>;
        readonly fixedSlotEntityIds: ReadonlySet<string>;
        readonly unsupportedConstraints: SketchParametricUnsupportedItem[];
        readonly relationBranchHintReaders: Map<string, () => SerializablePlainData>;
    }): void {
        if (input.constraintState.states.some((state) => (
            state === 'suppressed' || state === 'dangling' || state === 'broken'
        ))) {
            // 非活动关系保留在领域快照与诊断中，但不得进入活动方程集合。
            return;
        }

        const {
            system, constraintState, pointHandles, linePointHandles, entityPointHandles,
            centerPointHandles, radiusParameterIds, arcGeometryStates, curveLengthSegments, ellipseGeometryStates,
            implicitCurveDescriptors,
            splineCurveDescriptors, splineMirrorStructures,
            parabolaGeometryStates,
            slotStructureParameterIds, slotMemberPointIds, fixedPointIds,
            fixedEntityIds, fixedSlotEntityIds, unsupportedConstraints, relationBranchHintReaders,
        } = input;
        const report = (reason: string): void => {
            unsupportedConstraints.push({
                id: constraintState.constraintId,
                kind: constraintState.relationKind,
                reason,
            });
        };
        /** 直接目标里的实体 ID（有序；`targetEntityIds` 含 island 图影响范围，不能替代）。 */
        const resolveDirectEntityIds = (): readonly string[] => (constraintState.targetRefs
            ?.filter((ref) => ref.targetKind === 'entity')
            .map((ref) => ref.targetId)
            ?? constraintState.targetEntityIds);
        /** 直接目标原始顺序；通用阵列允许点与不同实体混合。 */
        const resolveDirectTargetRefs = () => constraintState.targetRefs ?? [
            ...constraintState.targetPointIds.map((targetId) => ({targetKind: 'point' as const, targetId})),
            ...constraintState.targetEntityIds.map((targetId) => ({targetKind: 'entity' as const, targetId})),
        ];
        /** 直接目标里的点句柄（有序）。 */
        const resolveDirectPointHandles = (): readonly SketchParametricPointHandle[] => {
            const directPointIds = constraintState.targetRefs
                ? constraintState.targetRefs
                    .filter((ref) => ref.targetKind === 'point')
                    .map((ref) => ref.targetId)
                : constraintState.targetPointIds;

            return directPointIds
                .map((pointId) => pointHandles.get(pointId))
                .filter((handle): handle is SketchParametricPointHandle => Boolean(handle));
        };

        /**
         * 所有活动关系统一通过可注入公式注册表编译。
         *
         * `compiled/rejected` 都表示该关系已被注册表完整处理；未注册关系显式进入
         * unsupported，禁止在 builder 内继续维护第二套 switch 公式实现。
         */
        const registryResult = this.constraintFormulaRegistry.compile({
            system,
            constraintState,
            linePointHandles,
            entityPointHandles,
            pointHandles,
            centerPointHandles,
            radiusParameterIds,
            arcGeometryStates,
            curveLengthSegments,
            ellipseGeometryStates,
            implicitCurveDescriptors,
            splineCurveDescriptors,
            splineMirrorStructures,
            parabolaGeometryStates,
            slotStructureParameterIds,
            slotMemberPointIds,
            fixedPointIds,
            fixedEntityIds,
            fixedSlotEntityIds,
            resolveDirectEntityIds,
            resolveDirectTargetRefs,
            resolveDirectPointHandles,
            reportUnsupported: report,
            registerResolvedBranchHint: (reader) => {
                relationBranchHintReaders.set(constraintState.constraintId, reader);
            },
        });

        if (registryResult === 'compiled' || registryResult === 'rejected') {
            return;
        }
        report('统一参数公式注册表尚未覆盖该关系类型');
    }
}
